import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { LabelService } from '../../services/label.service';
import { CalendarGoogle } from '../../models/doctor-model';
import { newCalendarEvent } from '../../models/calendar-model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DATE_FORMATS,
  DateAdapter,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import moment from 'moment';
import { DateInputDirective } from '../../shared/directives/date-input.directive';
import { DatePipe } from '@angular/common';
import { SharedModule } from '../shared.module';

// Define custom date format
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-panel-calendar-event',
  templateUrl: './panel-calendar-event.component.html',
  styleUrls: ['./panel-calendar-event.component.scss'],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    //{ provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'it' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
  standalone: false,
})
export class PanelCalendarEventComponent implements OnInit, OnChanges {
  @Input() patients: any[] = [];
  @Input() newAppointmentDay: string = '';
  @Input() newAppointmentMonth: string = '';
  @Input() newAppointmentYear: string = '';
  @Input() newAppointmentHours: string = '';
  @Input() newAppointmentMinutes: string = '';
  @Input() newAppointmentDate: string = '';
  @Input() newAppointmentTime: string = '';
  @Input() newAppointmentLenght: string = '45';
  @Input() calendars: CalendarGoogle[] = [];
  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<newCalendarEvent>();

  eventForm: FormGroup;
  isAppointmentAllDay: boolean = false;
  isAppointmentSelected: boolean = true;
  isAppointmentRemote: boolean = false;
  patientName: string = '';
  patientId: string = '';
  calendarName: string = '';
  calendarId: string = '';
  filteredPatients: any[] = [];
  newAppointmentTitle: string = '';
  isNewAppointmentRemote: boolean = false;
  isNewAppointmentSelected: boolean = false;
  isPsicogeniusSelected: boolean = false;
  areAllTheFieldsWritten: boolean = true;
  isPatientNameChecked: boolean = true;
  isCalendarChecked: boolean = true;
  isTitleChecked: boolean = true;
  isDayChecked: boolean = true;
  isMonthChecked: boolean = true;
  isYearChecked: boolean = true;
  isHoursChecked: boolean = true;
  isMinutesChecked: boolean = true;
  isLenghtChecked: boolean = true;
  title: string = 'Nuovo Evento';

  isBtnClicked: boolean = false;

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>,
    private datePipe: DatePipe
  ) {
    this.eventForm = this.fb.group({
      isAppointment: [true],
      patientName: [''],
      patientId: [''],
      calendarName: [''],
      calendarId: [''],
      title: ['', Validators.required],
      date: ['', [Validators.required]],
      startTime: [
        '',
        [
          Validators.required,
          Validators.pattern(/^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/),
        ],
      ],
      length: [
        '',
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      ],
      isRemote: [false],
      isAllDay: [false],
    });

    // Set the locale for the date adapter
    this.dateAdapter.setLocale('it');
    moment.locale('it'); // Set moment locale to Italian
  }
  ngOnInit(): void {
    // Inizializza i campi del form con gli input passati
    this.populateFormFields();
    // Ascolta i cambiamenti del campo isAppointment
    // Ascolta i cambiamenti del campo isAppointment
    this.eventForm
      .get('isAppointment')
      ?.valueChanges.subscribe((isAppointment) => {
        this.togglePatientNameValidator(isAppointment);
        this.toggleCalendarNameValidator(isAppointment);
      });

    // Inizializza con il valore di isAppointment
    const isAppointment = this.eventForm.get('isAppointment')?.value;
    this.togglePatientNameValidator(isAppointment);
    this.toggleCalendarNameValidator(isAppointment);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se uno degli input cambia, aggiorna i campi del form
    if (
      changes['newAppointmentDay'] ||
      changes['newAppointmentMonth'] ||
      changes['newAppointmentYear'] ||
      changes['newAppointmentHours'] ||
      changes['newAppointmentMinutes']
    ) {
      this.populateFormFields();
    }
  }

  togglePatientNameValidator(isAppointment: boolean): void {
    const patientNameControl = this.eventForm.get('patientName');
    if (isAppointment) {
      patientNameControl?.setValidators([Validators.required]);
      if (this.patients.length === 0) {
        patientNameControl?.disable();
      }
    } else {
      patientNameControl?.clearValidators();
    }
    patientNameControl?.updateValueAndValidity();
  }

  toggleCalendarNameValidator(isAppointment: boolean): void {
    const calendarNameControl = this.eventForm.get('calendarName');
    if (!isAppointment) {
      calendarNameControl?.setValidators([Validators.required]);
    } else {
      calendarNameControl?.clearValidators();
    }
    calendarNameControl?.updateValueAndValidity();
  }

  // Funzione per popolare i campi del form con gli input
  populateFormFields(): void {
    const formattedDate = moment(
      `${this.newAppointmentDay}/${this.newAppointmentMonth}/${this.newAppointmentYear}`,
      'DD/MM/YYYY'
    );

    // Concatenazione dell'orario
    let formattedTime = `${this.newAppointmentHours}:${this.newAppointmentMinutes}`;
    if (formattedTime === ':') {
      formattedTime = '00:00';
    }
    // Imposta i valori nel form
    this.eventForm.patchValue({
      patientName: this.patientName || '',
      patientId: this.patientId || '',
      calendarName: this.calendarName || '',
      calendarId: this.calendarId || '',
      title: this.newAppointmentTitle || '',
      date: formattedDate,
      startTime: formattedTime,
      length: this.newAppointmentLenght || '45',
      isRemote: this.isAppointmentRemote,
      isAllDay: this.isAppointmentAllDay,
    });
  }

  filterPatients(): void {
    const searchLower = this.eventForm.get('patientName')?.value.toLowerCase();
    this.filteredPatients = this.patients.filter(
      (patient) =>
        patient.first_name.toLowerCase().includes(searchLower) ||
        patient.last_name.toLowerCase().includes(searchLower)
    );
  }

  showAllPatients(): void {
    this.filteredPatients = this.patients;
  }

  selectPatient(patient: any): void {
    this.eventForm.patchValue({
      patientName: `${patient.first_name} ${patient.last_name}`,
      title: `${patient.first_name} ${patient.last_name}: Appuntamento N° ${patient.count}`,
      patientId: patient.id,
    });
    this.filteredPatients = [];
  }

  selectCalendar(calendar: CalendarGoogle) {
    if (calendar) {
      this.eventForm.patchValue({
        calendarName: calendar.summary,
        calendarId: calendar.id,
      });
      this.isPsicogeniusSelected = calendar.summary === 'Psicogenius';
    } else {
      this.eventForm.patchValue({
        calendarName: '',
        calendarId: '',
      });
      this.isPsicogeniusSelected = false;
    }
  }

  onCancel() {
    this.cancel.emit();
  }

  onConfirm() {
    if (this.isBtnClicked) {
      return;
    }

    if (this.eventForm.get('isAppointment')?.value) {
      this.eventForm
        .get('calendarId')
        ?.setValue(sessionStorage.getItem('PsicogeniusCalendarId') || '');
    }
    const formValues = this.eventForm.value;

    const output: newCalendarEvent = {
      date: this.formatDate(formValues.date) || '', // Formatta la data
      time: formValues.startTime,
      lenght: formValues.length,
      title: formValues.title,
      patientName: formValues.patientName,
      patientId: formValues.patientId,
      calendarName: formValues.calendarName,
      calendarId: formValues.calendarId,
      isAppointment: formValues.isAppointment,
      isRemote: formValues.isRemote,
      isAllDay: formValues.isAppointment ? false : formValues.isAllDay,
    };
    this.isBtnClicked = true;
    this.confirm.emit(output);
  }

  // Funzione per formattare la data in dd/MM/yyyy
  formatDate(date: any): string | null {
    if (date) {
      return this.datePipe.transform(date, 'dd/MM/yyyy'); // Usa Angular's DatePipe
    }
    return null;
  }
}
